import { useMemo } from "react";
import { Check, Circle } from "lucide-react";

interface PasswordRequirementsProps {
  password: string;
  isDark: boolean;
}

interface Requirement {
  label: string;
  met: boolean;
}

export const PasswordRequirements = ({ password, isDark }: PasswordRequirementsProps) => {
  const requirements: Requirement[] = useMemo(() => [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { label: "Contains a number", met: /\d/.test(password) },
    { label: "Contains special character", met: /[^A-Za-z0-9]/.test(password) },
  ], [password]);

  return (
    <div className="space-y-1.5 text-sm">
      <p className="font-medium" style={{ color: isDark ? "#d1d5db" : "#374151" }}>
        Password requirements:
      </p>
      {requirements.map((req) => (
        <div key={req.label} className="flex items-center gap-2">
          {req.met ? (
            <Check className="h-4 w-4 text-green-500 shrink-0" />
          ) : (
            <Circle
              className="h-4 w-4 shrink-0"
              style={{ color: isDark ? "#6b7280" : "#9ca3af" }}
            />
          )}
          <span
            style={{
              color: req.met
                ? (isDark ? "#4ade80" : "#16a34a")
                : (isDark ? "#9ca3af" : "#6b7280"),
            }}
          >
            {req.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export const isPasswordValid = (password: string): boolean => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
};
